import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabaseClient'
import { useAuth } from '@/hooks/useAuth'
import { UserPreferences, UserPreferencesUpdate, DEFAULT_PREFERENCES } from '@/types/preferences'

export function useUserPreferences() {
  const { user } = useAuth()
  const [preferences, setPreferences] = useState<UserPreferences | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Load preferences from database
  const loadPreferences = async () => {
    if (!user?.id) {
      setIsLoading(false)
      return
    }

    try {
      setError(null)
      const { data, error: fetchError } = await supabase
        .from('user_preferences')
        .select('*')
        .eq('user_id', user.id)
        .single()

      if (fetchError && fetchError.code !== 'PGRST116') { // PGRST116 = no rows returned
        throw fetchError
      }

      if (data) {
        setPreferences(data)
      } else {
        // Create default preferences for new user
        const defaultPrefs = {
          user_id: user.id,
          ...DEFAULT_PREFERENCES
        }

        const { data: newData, error: createError } = await supabase
          .from('user_preferences')
          .upsert(defaultPrefs, { onConflict: 'user_id' })
          .select()
          .single()

        if (createError) throw createError
        setPreferences(newData)
      }
    } catch (err) {
      console.error('Error loading user preferences:', err)
      setError(err instanceof Error ? err.message : 'Failed to load preferences')
    } finally {
      setIsLoading(false)
    }
  }

  // Update preferences in database
  const updatePreferences = async (updates: UserPreferencesUpdate): Promise<boolean> => {
    if (!user?.id || !preferences) {
      setError('User not authenticated')
      return false
    }

    try {
      setError(null)
      const { data, error: updateError } = await supabase
        .from('user_preferences')
        .update(updates)
        .eq('user_id', user.id)
        .select()
        .single()

      if (updateError) throw updateError

      setPreferences(data)
      return true
    } catch (err) {
      console.error('Error updating preferences:', err)
      setError(err instanceof Error ? err.message : 'Failed to update preferences')
      return false
    }
  }

  // Reset preferences to defaults
  const resetToDefaults = async (): Promise<boolean> => {
    return updatePreferences(DEFAULT_PREFERENCES)
  }

  // Load preferences when user changes
  useEffect(() => {
    if (!user?.id) {
      // Clear preferences immediately when user logs out
      setPreferences(null)
      setIsLoading(false)
      setError(null)
      return
    }
    loadPreferences()
  }, [user?.id])

  return {
    preferences,
    isLoading,
    error,
    updatePreferences,
    resetToDefaults,
    refresh: loadPreferences
  }
}